import { useEffect, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import type { AnalyticsApi, AnalyticsRange } from '../../api/analytics';
import type { AnalyticsTopRow } from '../../api/types';
import { ErrorNote } from '../../components/ErrorNote';
import { Spinner } from '../../components/Spinner';
import { useLoad } from '../../lib/useLoad';
import { useTimeFormat } from '../../lib/tenantTime';
import {
  channelLabel,
  compact,
  dimensionColumn,
  dimensionLabel,
  duration,
  exact,
  flagOf,
  money,
  percent,
} from './analyticsFormat';
import { DETAIL_LIMIT, DETAIL_TABLES, FALLBACK_PAGE_SIZE, type DetailTable } from './detail';
import { downloadCsv } from './exports';

/**
 * A card, opened out — the shell, the ranked tables, and the visits list.
 *
 * WHICH cards open and what into is `detail.ts`; this file is how it is drawn.
 */

export function CardDetail({
  api,
  range,
  cardId,
  title,
  siteName,
  rangeLabel,
  tables,
  covered = false,
  onOpenSession,
  onClose,
  children,
}: {
  api: AnalyticsApi;
  range: AnalyticsRange;
  cardId: string;
  title: string;
  siteName: string;
  /** The range as the page header states it — the server echoes back what it
   *  actually answered, which is not always the civil days we asked for. */
  rangeLabel: string;
  /**
   * Replaces the configured tables. One caller needs it: the geography card
   * drilled into a country asks for that country's CITIES, and the drill is the
   * screen's own state so that the card behind and this panel agree on it.
   */
  tables?: DetailTable[];
  /**
   * Something is open ON TOP of this — a visit opened from the table below.
   * Two overlays each listening on `window` both hear the same Escape, and the
   * one that should answer is the one in front, so this one stands down while
   * it is covered. The screen owns both, so it is the only thing that can say.
   */
  covered?: boolean;
  /** Opening a visit from here raises the drawer OVER this panel. */
  onOpenSession: (key: string) => void;
  onClose: () => void;
  /** The card's own drawing, at the size the dashboard could not give it. */
  children?: ReactNode;
}) {
  useEffect(() => {
    if (covered) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, covered]);

  const shown = tables ?? DETAIL_TABLES[cardId] ?? [];

  return (
    <div
      className="an-modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section className="an-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="an-modal-head">
          <div>
            <div className="an-title">{title}</div>
            <div className="an-sub">
              {siteName} · {rangeLabel}
            </div>
          </div>
          <button type="button" className="btn btn-ghost" onClick={onClose} aria-label="Close">
            <X size={15} />
          </button>
        </div>
        <div className="an-modal-body">
          {children ? <div className="an-modal-figure">{children}</div> : null}
          {cardId === 'sessions' ? (
            <SessionsDetail api={api} range={range} siteName={siteName} onOpen={onOpenSession} />
          ) : (
            shown.map((table) => (
              <TopTable
                key={`${table.dim}:${table.country ?? ''}`}
                api={api}
                range={range}
                dim={table.dim}
                country={table.country}
                label={table.label}
                siteName={siteName}
              />
            ))
          )}
        </div>
      </section>
    </div>
  );
}

/**
 * One ranked dimension, every column the row carries.
 *
 * The booking pair is drawn only when something in this table has one. A site
 * that attributes no bookings at all would otherwise carry two columns of zeros
 * down every table on the screen, which reads as a broken join rather than as
 * the honest answer that nothing here converted.
 */
function TopTable({
  api,
  range,
  dim,
  country,
  label,
  siteName,
}: {
  api: AnalyticsApi;
  range: AnalyticsRange;
  dim: string;
  country?: string;
  label?: (value: string) => string;
  siteName: string;
}) {
  const load = useLoad(
    () => api.getAnalyticsTop(range, dim, DETAIL_LIMIT, country),
    [range, dim, country],
  );
  const rows: AnalyticsTopRow[] = load.data?.payload ?? [];
  const distinct = load.data?.stats.distinct ?? 0;
  const withBookings = rows.some((row) => row.bookings > 0);

  const csv = () =>
    downloadCsv(
      `${siteName}-${dim}${country ? `-${country}` : ''}-${range.from}-to-${range.to}.csv`,
      ['value', 'sessions', 'share', 'pageviews', 'bounce_rate', 'avg_engaged_seconds', 'bookings', 'booking_value'],
      rows.map((row) => [
        row.value,
        row.sessions,
        row.share,
        row.pageviews,
        row.bounceRate,
        row.avgEngagedSeconds,
        row.bookings,
        row.bookingValue,
      ]),
    );

  return (
    <div className="an-detail-block">
      <div className="an-detail-head">
        <h3 className="an-detail-title">{dimensionLabel(dim)}</h3>
        <span className="an-card-head-end">
          <span className="an-card-note">
            {/* The count is the whole reason a hundred rows is enough or is not:
                it says whether what you are reading is the list or the top of
                it, which the card itself could never tell you. */}
            {distinct > rows.length
              ? `Showing the top ${exact(rows.length)} of ${exact(distinct)}`
              : `${exact(rows.length)} in this range`}
          </span>
          {rows.length ? (
            <button
              type="button"
              className="an-card-export"
              title={`Download ${dimensionLabel(dim).toLowerCase()} as a spreadsheet`}
              aria-label={`Download ${dimensionLabel(dim).toLowerCase()} as a spreadsheet`}
              onClick={csv}
            >
              <Download size={13} />
            </button>
          ) : null}
        </span>
      </div>

      {load.error ? <ErrorNote error={load.error} /> : null}
      {load.loading && !load.data ? (
        <div className="an-empty">
          <Spinner />
        </div>
      ) : rows.length ? (
        <div className="an-table-scroll">
          <table className="an-table an-detail-table">
            <thead>
              <tr>
                <th className="an-rank">#</th>
                <th>{dimensionColumn(dim)}</th>
                <th className="an-num">Sessions</th>
                <th>Share</th>
                <th className="an-num">Pages</th>
                <th className="an-num">Bounce</th>
                <th className="an-num">Engaged</th>
                {withBookings ? <th className="an-num">Reservations</th> : null}
                {withBookings ? <th className="an-num">Value</th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.value}>
                  <td className="an-rank">{index + 1}</td>
                  <td className="an-detail-value" title={row.value}>
                    {label ? label(row.value) : row.value}
                  </td>
                  <td className="an-num">{exact(row.sessions)}</td>
                  <td className="an-share-cell">
                    <span className="an-bar-track">
                      <span
                        className="an-bar-fill"
                        style={{ width: `${Math.max(1, row.share * 100)}%`, background: 'var(--viz-1)' }}
                      />
                    </span>
                    <span>{percent(row.share)}</span>
                  </td>
                  <td className="an-num">{compact(row.pageviews)}</td>
                  <td className="an-num">{percent(row.bounceRate)}</td>
                  <td className="an-num">{duration(row.avgEngagedSeconds)}</td>
                  {withBookings ? <td className="an-num">{row.bookings || '—'}</td> : null}
                  {withBookings ? (
                    <td className="an-num">{row.bookingValue ? money(row.bookingValue) : '—'}</td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="an-empty">Nothing recorded for this in the range.</div>
      )}
    </div>
  );
}

const OUTCOMES = [
  { key: '', label: 'Every visit' },
  { key: 'booking', label: 'Booked' },
  { key: 'lead', label: 'Left a quote' },
  { key: 'none', label: 'Neither' },
] as const;

/**
 * Recent visits, all of them.
 *
 * The card shows twelve of the fifty it already fetched and cannot reach the
 * fifty-first. Here the page is whole and pages onward, and the two filters the
 * server already understands are offered. There is no anonymous-only filter
 * because there is no such query — `identified` narrows one way only, so the
 * control is a toggle rather than a third option that would answer wrongly.
 */
function SessionsDetail({
  api,
  range,
  siteName,
  onOpen,
}: {
  api: AnalyticsApi;
  range: AnalyticsRange;
  siteName: string;
  onOpen: (key: string) => void;
}) {
  const { fmtDateTime } = useTimeFormat();
  const [page, setPage] = useState(1);
  const [outcome, setOutcome] = useState<string>('');
  const [identifiedOnly, setIdentifiedOnly] = useState(false);

  const load = useLoad(
    () =>
      api.listAnalyticsSessions(range, {
        page,
        outcome: outcome || undefined,
        identified: identifiedOnly ? 'true' : undefined,
      }),
    [range, page, outcome, identifiedOnly],
  );

  const rows = load.data?.payload ?? [];
  const total = load.data?.stats.total ?? 0;
  const pageSize = load.data?.stats.pageSize ?? FALLBACK_PAGE_SIZE;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  // A filter answers a different question, so it starts at its own first page —
  // page 3 of an unfiltered list is page 3 of nothing once it narrows.
  const narrow = (apply: () => void) => {
    apply();
    setPage(1);
  };

  const csv = () =>
    downloadCsv(
      `${siteName}-visits-${range.from}-to-${range.to}-p${page}.csv`,
      ['started_at', 'who', 'channel', 'entry_path', 'exit_path', 'pageviews', 'engaged_seconds', 'country', 'city', 'outcome', 'booking_value'],
      rows.map((row) => [
        row.startedAt,
        row.contactName ?? (row.isIdentified ? 'signed in' : 'anonymous'),
        row.channel,
        row.entryPath,
        row.exitPath,
        row.pageviews,
        row.engagedSeconds,
        row.country,
        row.city,
        row.outcome,
        row.bookingValue,
      ]),
    );

  return (
    <div className="an-detail-block">
      <div className="an-detail-head">
        <div className="an-seg" role="group" aria-label="Outcome">
          {OUTCOMES.map((entry) => (
            <button
              key={entry.key || 'all'}
              type="button"
              className={outcome === entry.key ? 'on' : ''}
              onClick={() => narrow(() => setOutcome(entry.key))}
            >
              {entry.label}
            </button>
          ))}
        </div>
        <div className="an-seg" role="group" aria-label="Who">
          <button
            type="button"
            className={identifiedOnly ? 'on' : ''}
            onClick={() => narrow(() => setIdentifiedOnly((was) => !was))}
            aria-pressed={identifiedOnly}
          >
            Signed in only
          </button>
        </div>
        <span className="an-card-head-end">
          <span className="an-card-note">{exact(total)} in this range</span>
          {rows.length ? (
            <button
              type="button"
              className="an-card-export"
              title="Download this page of visits as a spreadsheet"
              aria-label="Download this page of visits as a spreadsheet"
              onClick={csv}
            >
              <Download size={13} />
            </button>
          ) : null}
        </span>
      </div>

      {load.error ? <ErrorNote error={load.error} /> : null}
      {load.loading && !load.data ? (
        <div className="an-empty">
          <Spinner />
        </div>
      ) : rows.length ? (
        <div className="an-table-scroll">
          <table className="an-table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Who</th>
                <th>Came from</th>
                <th>Entered on</th>
                <th>Left from</th>
                <th className="an-num">Pages</th>
                <th className="an-num">Engaged</th>
                <th>Where</th>
                <th>Outcome</th>
                <th className="an-open" aria-label="Opens the visit" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.key}
                  tabIndex={0}
                  onClick={() => onOpen(row.key)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    onOpen(row.key);
                  }}
                >
                  <td>{fmtDateTime(row.startedAt)}</td>
                  <td>{row.contactName || (row.isIdentified ? 'Signed in' : 'Anonymous')}</td>
                  <td>{channelLabel(row.channel)}</td>
                  <td className="an-detail-value">{row.entryPath ?? '—'}</td>
                  <td className="an-detail-value">{row.exitPath ?? '—'}</td>
                  <td className="an-num">{row.pageviews}</td>
                  <td className="an-num">{duration(row.engagedSeconds)}</td>
                  <td>
                    {row.country ? `${flagOf(row.country)} ${row.city || row.country}`.trim() : '—'}
                  </td>
                  <td>
                    {row.outcome === 'booking'
                      ? `Booked${row.bookingValue ? ` · ${money(row.bookingValue)}` : ''}`
                      : row.outcome === 'lead'
                        ? 'Left a quote'
                        : '—'}
                  </td>
                  <td className="an-open">
                    <ChevronRight size={14} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="an-empty">No visits match this.</div>
      )}

      {pages > 1 ? (
        <div className="an-pager">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={page <= 1 || load.loading}
            onClick={() => setPage((was) => Math.max(1, was - 1))}
          >
            <ChevronLeft size={14} /> Newer
          </button>
          <span className="an-card-note">
            Page {page} of {pages}
          </span>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={page >= pages || load.loading}
            onClick={() => setPage((was) => Math.min(pages, was + 1))}
          >
            Older <ChevronRight size={14} />
          </button>
        </div>
      ) : null}
    </div>
  );
}
